import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { ColumnsField } from './fields/columns-field';
import { DocOptionsField } from './fields/doc-options-field';
import { FilenameField } from './fields/filename-field';
import { FormatField } from './fields/format-field';
import { LARGE_EXPORT_WARNING_THRESHOLD, LargeExportWarning } from './fields/large-export-warning';
import { ScopeField } from './fields/scope-field';
import { SourceDocsField } from './fields/source-docs-field';
import { StyleField } from './fields/style-field';
import type { DownloadDefinition, DownloadFormValues, DownloadVariant } from './types';
import { defaultDocOptions, defaultIconForKind, kindShowsFilename } from './types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definition: DownloadDefinition;
  onAsyncStarted?: (payload: {
    variantId: string;
    exportId: string;
    jobId?: string;
    format?: DownloadFormValues['format'];
  }) => void;
}

interface VariantState {
  filename: string;
  format: string;
  columns: string[];
  relativityColumns: string[];
  style: string;
  docOptions: ReturnType<typeof defaultDocOptions>;
  includeSourceDocuments: boolean;
  orderByDate: boolean;
}

function makeInitialState(variant: DownloadVariant): VariantState {
  const firstFormat = variant.formats[0];
  return {
    filename: variant.defaultFilename ?? '',
    format: firstFormat ? String(firstFormat) : '',
    columns: variant.columns?.default ?? [],
    relativityColumns: [],
    style: '',
    docOptions: defaultDocOptions(),
    includeSourceDocuments: false,
    orderByDate: false,
  };
}

export const DownloadModal = ({ open, onOpenChange, definition, onAsyncStarted }: Props) => {
  const { variants, title = 'Download', scopes, defaultScopeId } = definition;
  const [selectedScopeId, setSelectedScopeId] = useState(defaultScopeId ?? 'all');
  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    () => definition.defaultVariantId ?? variants[0]?.id ?? '',
  );
  const [stateByVariant, setStateByVariant] = useState<Record<string, VariantState>>(() => {
    const initial: Record<string, VariantState> = {};
    for (const v of variants) initial[v.id] = makeInitialState(v);
    return initial;
  });
  const [largeExportAcknowledged, setLargeExportAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'variant' | 'form'>(variants.length > 1 ? 'variant' : 'form');

  const variant = useMemo(
    () => variants.find((v) => v.id === selectedVariantId) ?? variants[0],
    [variants, selectedVariantId],
  );

  useEffect(() => {
    if (!open) {
      setLargeExportAcknowledged(false);
      setIsSubmitting(false);
      const reset: Record<string, VariantState> = {};
      for (const v of variants) reset[v.id] = makeInitialState(v);
      setStateByVariant(reset);
      setSelectedVariantId(definition.defaultVariantId ?? variants[0]?.id ?? '');
      setSelectedScopeId(defaultScopeId ?? 'all');
      setStep(variants.length > 1 ? 'variant' : 'form');
    }
  }, [open, variants, definition.defaultVariantId, defaultScopeId]);

  if (!variant) return null;

  const state = stateByVariant[variant.id] ?? makeInitialState(variant);
  const patch = (next: Partial<VariantState>) =>
    setStateByVariant((prev) => ({
      ...prev,
      [variant.id]: { ...(prev[variant.id] ?? makeInitialState(variant)), ...next },
    }));

  const showFilename = kindShowsFilename(variant.kind);
  const showFormat = variant.formats.length > 1;
  const selectedScope = scopes?.find((s) => s.id === selectedScopeId);
  const warningTotalCount = selectedScope?.totalCount ?? definition.totalCount;
  const showLargeExportWarning =
    variant.largeExportWarning === true &&
    warningTotalCount !== undefined &&
    warningTotalCount > LARGE_EXPORT_WARNING_THRESHOLD;

  const disabledReason =
    showLargeExportWarning && !largeExportAcknowledged
      ? 'Please acknowledge that this export may take a while before continuing.'
      : undefined;

  const handleDownload = async () => {
    if (disabledReason) return;
    const values: DownloadFormValues = {
      filename: state.filename,
      format: state.format as DownloadFormValues['format'],
      scope: scopes ? selectedScopeId : undefined,
      columns: variant.columns ? state.columns : undefined,
      relativityColumns:
        variant.columns?.relativity && state.relativityColumns.length > 0 ? state.relativityColumns : undefined,
      style: variant.style ? state.style : undefined,
      docOptions: variant.docOptions ? state.docOptions : undefined,
      includeSourceDocuments: variant.sourceDocs ? state.includeSourceDocuments : undefined,
      orderByDate: variant.sourceDocs && state.includeSourceDocuments ? state.orderByDate : undefined,
    };

    setIsSubmitting(true);
    try {
      if (variant.mode === 'async') {
        const { exportId, jobId } = await variant.mutate(values);
        onOpenChange(false);
        onAsyncStarted?.({
          variantId: variant.id,
          exportId,
          jobId,
          format: values.includeSourceDocuments ? undefined : values.format,
        });
      } else {
        await variant.mutate(values);
        onOpenChange(false);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Download failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showScopeControl = !!scopes && scopes.length > 1;
  const showIncludedItemsNotice = selectedScopeId !== 'selected' && selectedScope?.totalCount !== undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {step === 'variant' ? (
            <div className="flex flex-row justify-center gap-4">
              {variants.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    setSelectedVariantId(v.id);
                    setStep('form');
                  }}
                  className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50/50 p-4 transition-all duration-200 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50"
                >
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 text-lg text-blue-600">
                    <Icon className={v.icon ?? defaultIconForKind(v.kind)} />
                  </div>
                  <span className="mb-0.5 text-base font-medium text-gray-700">{v.label}</span>
                  {v.description && <span className="text-center text-xs text-gray-400">{v.description}</span>}
                </button>
              ))}
            </div>
          ) : (
            <>
              {showScopeControl && scopes && (
                <ScopeField value={selectedScopeId} onChange={setSelectedScopeId} scopes={scopes} />
              )}
              {showIncludedItemsNotice && selectedScope?.totalCount !== undefined && (
                <div className="flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-600">
                  <Icon className="lucide--info" />
                  This download will include {selectedScope.totalCount.toLocaleString()} item
                  {selectedScope.totalCount !== 1 ? 's' : ''}
                </div>
              )}

              {showLargeExportWarning && warningTotalCount !== undefined && (
                <LargeExportWarning
                  totalCount={warningTotalCount}
                  acknowledged={largeExportAcknowledged}
                  onAcknowledgedChange={setLargeExportAcknowledged}
                />
              )}

              <div className="flex flex-col gap-4">
                {showFilename && <FilenameField value={state.filename} onChange={(filename) => patch({ filename })} />}

                {variant.style && <StyleField value={state.style} onChange={(style) => patch({ style })} />}

                {variant.columns && (
                  <ColumnsField
                    spec={variant.columns}
                    columns={state.columns}
                    onColumnsChange={(columns) => patch({ columns })}
                    relativityColumns={state.relativityColumns}
                    onRelativityColumnsChange={(relativityColumns) => patch({ relativityColumns })}
                  />
                )}

                {showFormat && (
                  <FormatField
                    value={state.format as DownloadFormValues['format']}
                    onChange={(format) => patch({ format: String(format) })}
                    options={variant.formats}
                  />
                )}

                {variant.docOptions && (
                  <DocOptionsField value={state.docOptions} onChange={(docOptions) => patch({ docOptions })} />
                )}

                {variant.sourceDocs && (
                  <SourceDocsField
                    includeSourceDocuments={state.includeSourceDocuments}
                    orderByDate={state.orderByDate}
                    onIncludeSourceDocumentsChange={(v) =>
                      patch({ includeSourceDocuments: v, orderByDate: v ? state.orderByDate : false })
                    }
                    onOrderByDateChange={(v) => patch({ orderByDate: v })}
                    showEmailNotification
                  />
                )}
              </div>

              <DialogFooter className="gap-2">
                {variants.length > 1 && (
                  <Button variant="outline" onClick={() => setStep('variant')} disabled={isSubmitting}>
                    <Icon className="lucide--arrow-left" />
                    Back
                  </Button>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-1">
                      <Button
                        variant="primary"
                        className="w-full"
                        onClick={handleDownload}
                        disabled={!!disabledReason || isSubmitting}
                      >
                        {isSubmitting ? 'Preparing...' : 'Download'}
                        <Icon className="lucide--download" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
                </Tooltip>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
